import { useState, useEffect } from "react";

/**
 * Persists dark-mode preference in localStorage and toggles
 * a `data-theme="dark"` attribute on <html> so CSS can react.
 */
export default function useDarkMode() {
  // SSR/prerender-safe: start with deterministic default (dark = true).
  // localStorage is read inside useEffect after hydration, avoiding mismatch
  // when a user has saved a preference different from the default.
  const [dark, setDark] = useState(true);

  useEffect(() => {
    // Skip localStorage read during prerender so prerendered HTML matches
    // the client's first render exactly (avoids React #418/#423).
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("HeadlessChrome")) {
      return;
    }
    try {
      const saved = localStorage.getItem("theme");
      if (saved) setDark(saved === "dark");
    } catch {
      /* storage unavailable */
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.setAttribute("data-theme", "dark");
    } else {
      root.removeAttribute("data-theme");
    }
    try {
      localStorage.setItem("theme", dark ? "dark" : "light");
    } catch {
      /* storage full / unavailable */
    }
  }, [dark]);

  const toggle = () => setDark((d) => !d);

  return { dark, toggle };
}
